import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Container, Download, Hourglass, Package } from "lucide-react";
import { Seo, SITE_URL } from "@/components/Seo";
import { SpecTable } from "@/components/SpecTable";
import { Reveal } from "@/components/Reveal";
import { downloadSpecSheet } from "@/lib/specSheet";
import { useLang, usePath } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GradePage() {
  const { code } = useParams();
  const { t } = useLang();
  const lp = usePath();
  const P = t.products;
  const G = t.gradePg;
  const [products, setProducts] = useState(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    setProducts(null);
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [code]);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const product = useMemo(() => products?.find((p) => p.code === code), [products, code]);

  const similar = useMemo(() => {
    if (!products || !product) return [];
    const idx = products.findIndex((p) => p.code === code);
    const neighbours = [products[idx - 1], products[idx + 1]].filter(Boolean);
    const sameColour = products.filter((p) => p.code !== code && p.color === product.color);
    const merged = [...neighbours, ...sameColour];
    return merged.filter((p, i) => merged.findIndex((x) => x.code === p.code) === i).slice(0, 3);
  }, [products, product, code]);

  if (products === null) return <div className="min-h-screen bg-clara-espresso" />;
  if (!product) return <Navigate to={lp("/grades")} replace />;

  const p = product;
  const colour = P.colours[p.color];
  const L = P.specLabels;
  const tv = (v) => P.values[v] || v;
  const contactUrl = `${lp("/contact")}?grade=${p.code}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `Clara Cocoa Powder ${p.code}`,
    image: p.image?.startsWith("/") ? `${SITE_URL}${p.image}` : p.image,
    url: `${SITE_URL}/en/grades/${p.code}`,
    category: "Cocoa Powder",
    brand: { "@type": "Brand", name: "Clara" },
    manufacturer: { "@id": `${SITE_URL}/#org` },
    description: `${p.type} cocoa powder — ${p.color}, pH ${p.ph}. Packaged in 25 kg 3-ply kraft paper bags, 2-year shelf life, flexible minimum order (less-than-container loads welcome).`,
    additionalProperty: [
      { "@type": "PropertyValue", name: "Process", value: p.type },
      { "@type": "PropertyValue", name: "Colour", value: p.color },
      { "@type": "PropertyValue", name: "pH", value: p.ph },
      { "@type": "PropertyValue", name: "Fat Content", value: p.physical.fat_content },
      { "@type": "PropertyValue", name: "Fineness", value: p.physical.fineness },
      { "@type": "PropertyValue", name: "Moisture", value: p.physical.moisture },
      { "@type": "PropertyValue", name: "Shell Content", value: p.physical.shell_content },
    ],
  };

  return (
    <div data-testid="grade-page" className="bg-clara-espresso text-clara-cream noise-overlay">
      <Seo title={G.seoTitle(p, colour)} description={G.seoDesc(p, colour)} path={`/grades/${p.code}`} image={p.image} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to={lp("/grades")} data-testid="grade-back-link" className="inline-flex items-center gap-2 font-data text-xs tracking-[0.2em] uppercase text-clara-cream/60 hover:text-clara-cream transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> {G.backToRange}
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden border border-clara-cream/15 aspect-[4/5] w-full max-h-[560px]"
              style={{ backgroundColor: p.hex }}
            >
              <img src={p.image} alt={`Clara ${p.code} cocoa powder`} className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
            <div>
              <Reveal>
                <p className="eyebrow text-clara-terracotta mb-4">{p.type} · {colour}</p>
                <h1 className="font-display font-light text-7xl lg:text-9xl leading-[0.9] tracking-tighter mb-8" data-testid="grade-code-heading">{p.code}</h1>
                <div className="flex flex-wrap gap-3 mb-10 font-data text-xs">
                  <span className="px-4 py-1.5 rounded-full bg-clara-cream/10 border border-clara-cream/20 uppercase tracking-wider">{p.type}</span>
                  <span className="px-4 py-1.5 rounded-full bg-clara-cream/10 border border-clara-cream/20">{colour}</span>
                  <span className="px-4 py-1.5 rounded-full bg-clara-cream/10 border border-clara-cream/20">pH {p.ph}</span>
                </div>
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-3">{G.bestFor}</p>
                <div className="flex flex-wrap gap-2 mb-10" data-testid="grade-best-for">
                  {(p.applications || []).map((a) => (
                    <span key={a} className="px-4 py-1.5 rounded-full border border-clara-terracotta/50 text-xs text-clara-cream/90">{P.appLabels[a]}</span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={contactUrl} data-testid="grade-request-sample-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-clara-terracotta px-7 py-4 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors duration-300">
                    {G.sampleCta} {p.code} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                  <button onClick={() => downloadSpecSheet(p)} data-testid="grade-download-tds-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-clara-cream/30 px-7 py-4 text-sm font-semibold text-clara-cream hover:border-clara-cream transition-colors duration-300">
                    <Download className="w-4 h-4" /> {G.downloadTds}
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 lg:py-20 border-t border-clara-cream/10" data-testid="grade-specs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12">
            <SpecTable title={P.physicalTitle} rows={[
              [L.Color, colour], [L.pH, p.ph], [L.Flavor, tv(p.physical.flavor)],
              [L["Fat Content"], p.physical.fat_content], [L.Fineness, p.physical.fineness],
              [L.Moisture, p.physical.moisture], [L["Shell Content"], p.physical.shell_content],
            ]} />
            <SpecTable title={P.microTitle} rows={[
              [L["Total Plate Count"], p.microbiological.total_plate_count], [L.Yeast, p.microbiological.yeast],
              [L.Mold, p.microbiological.mold], [L.Enterobacteriaceae, tv(p.microbiological.enterobacteriaceae)],
              [L.Salmonella, tv(p.microbiological.salmonella)], [L["E. Coli"], tv(p.microbiological.e_coli)],
              [L.Coliform, tv(p.microbiological.coliform)],
            ]} />
          </div>
          <p className="font-data text-[10px] text-clara-darkmuted italic">{P.note}</p>
        </div>
      </section>

      {/* Packaging & MOQ */}
      <section className="pb-16 lg:pb-20" data-testid="grade-logistics">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid sm:grid-cols-3 gap-px bg-clara-cream/15 border border-clara-cream/15">
            <div className="bg-clara-espresso p-8 flex items-start gap-5">
              <Package className="w-7 h-7 text-clara-terracotta shrink-0" strokeWidth={1.25} />
              <div>
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-2">{G.packLabel}</p>
                <p className="font-display text-2xl" data-testid="grade-packaging">{G.packValue}</p>
              </div>
            </div>
            <div className="bg-clara-espresso p-8 flex items-start gap-5">
              <Hourglass className="w-7 h-7 text-clara-terracotta shrink-0" strokeWidth={1.25} />
              <div>
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-2">{G.shelfLabel}</p>
                <p className="font-display text-2xl" data-testid="grade-shelf-life">{G.shelfValue}</p>
              </div>
            </div>
            <div className="bg-clara-espresso p-8 flex items-start gap-5">
              <Container className="w-7 h-7 text-clara-terracotta shrink-0" strokeWidth={1.25} />
              <div>
                <p className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted mb-2">{G.moqLabel}</p>
                <p className="font-display text-2xl" data-testid="grade-moq">{G.moqValue}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar grades */}
      {similar.length > 0 && (
        <section className="pb-32" data-testid="grade-similar">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-8">{G.similar}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
              {similar.map((s) => (
                <Link key={s.code} to={lp(`/grades/${s.code}`)} data-testid={`similar-grade-${s.code}`}
                  className="group relative overflow-hidden border border-clara-cream/15 aspect-[4/3]" style={{ backgroundColor: s.hex }}>
                  <img src={s.image} alt={`Clara ${s.code} cocoa powder`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <div>
                      <div className="font-display text-2xl text-clara-cream">{s.code}</div>
                      <div className="text-xs text-clara-cream/80">{P.colours[s.color]} · pH {s.ph}</div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-clara-cream/70 group-hover:text-clara-terracotta transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sticky sample CTA */}
      <motion.div
        initial={false}
        animate={{ y: showSticky ? 0 : 110 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        data-testid="sticky-sample-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-clara-espresso/95 backdrop-blur-md border-t border-clara-cream/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="font-data text-xs text-clara-cream/70 hidden sm:block">{p.code} · {colour} · pH {p.ph}</div>
          <div className="font-display text-xl sm:hidden">{p.code}</div>
          <Link to={contactUrl} data-testid="sticky-request-sample-btn" className="inline-flex items-center gap-2 rounded-full bg-clara-terracotta px-6 py-3 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors duration-300">
            {G.sampleCta} {p.code} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
